import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MoreVertical, Volume2, VolumeX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface RecordingPlayerProps {
  recordingUrl: string;
  recordingId: string;
}

export function RecordingPlayer({ recordingUrl, recordingId }: RecordingPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = async () => {
    try {
      setError(null);
      
      // If already playing, pause
      if (isPlaying && audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        return;
      }

      // If audio is already loaded, just play it
      if (audioRef.current) {
        await audioRef.current.play();
        setIsPlaying(true);
        return;
      }

      // Audio not loaded yet - fetch from edge function
      setIsLoading(true);
      
      // Get Twilio account SID from settings
      const { data: settings, error: settingsError } = await supabase
        .from('calie_tenants')
        .select('twilio_sid')
        .maybeSingle();

      if (settingsError || !settings || !settings.twilio_sid) {
        throw new Error('Failed to fetch Twilio account SID');
      }

      // Request recording data via Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('Calie_getRecording', {
        body: {
          action: 'getRecording',
          accountSid: settings.twilio_sid,
          recordingUrl,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch recording');
      }

      const objectUrl = `data:${data.contentType};base64,${data.base64Data}`;
      
      // Create and configure audio element
      const audio = new Audio(objectUrl);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
      });
      audio.addEventListener('error', () => {
        setIsPlaying(false);
        setError('Failed to play recording');
      });

      audioRef.current = audio;
      setIsLoading(false);
      await audioRef.current.play();
      setIsPlaying(true);

    } catch (error) {
      console.error('Error playing recording:', error);
      setError(error instanceof Error ? error.message : 'Failed to play recording');
      setIsPlaying(false);
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setError(null);
      
      // Get Twilio account SID from settings
      const { data: settings, error: settingsError } = await supabase
        .from('calie_tenants')
        .select('twilio_sid')
        .maybeSingle();

      if (settingsError || !settings || !settings.twilio_sid) {
        throw new Error('Failed to fetch Twilio account SID');
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const { data, error } = await supabase.functions.invoke('Calie_getRecording', {
        body: {
          action: 'getRecording',
          accountSid: settings.twilio_sid,
          recordingUrl,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch recording');
      }

      const href = `data:${data.contentType};base64,${data.base64Data}`;
      const a = document.createElement('a');
      a.href = href;
      a.download = `recording-${recordingId}.mp3`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

    } catch (error) {
      console.error('Error downloading recording:', error);
      setError(error instanceof Error ? error.message : 'Failed to download recording');
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const time = parseFloat(e.target.value);
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <div className="space-y-1">
      <div className="bg-muted rounded-lg p-2 flex items-center gap-2">
        <button
          onClick={handlePlayPause}
          disabled={isLoading}
          className="w-8 h-8 flex items-center justify-center disabled:opacity-50"
          aria-label={isLoading ? "Loading" : isPlaying ? "Pause" : "Play"}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : isPlaying ? (
            <span className="w-3 h-3 bg-foreground rounded-sm" />
          ) : (
            <div className="w-0 h-0 border-t-[6px] border-t-transparent border-l-[10px] border-l-foreground border-b-[6px] border-b-transparent ml-1" />
          )}
      </button>

        <div className="flex items-center gap-2 flex-1">
          <span className="text-xs text-muted-foreground min-w-[45px]">
            {formatTime(currentTime)}
          </span>
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="flex-1 h-1 bg-border rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-muted-foreground min-w-[45px]">
            {formatTime(duration || 0)}
          </span>
        </div>

        <button
          onClick={toggleMute}
          className="p-1 hover:bg-accent rounded"
        >
          {isMuted ? (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Volume2 className="h-4 w-4 text-muted-foreground" />
          )}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleDownload}>
              Download Recording
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}